import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiError } from "../lib/apiClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in
  const applyHue = (hue) => {
    if (typeof hue === "number") document.documentElement.style.setProperty("--hue", String(hue));
  };

  useEffect(() => {
    let active = true;
    api
      .get("/auth/me")
      .then(({ data }) => {
        if (!active) return;
        setUser(data);
        applyHue(data.hue);
      })
      .catch(() => active && setUser(false));
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    applyHue(data.hue);
    return data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    if (data && data.pending_approval) return data; // awaiting admin approval — do not log in
    setUser(data);
    applyHue(data.hue);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    setUser(false);
  }, []);

  const updateSettings = useCallback(async (payload) => {
    const { data } = await api.put("/settings", payload);
    setUser(data);
    applyHue(data.hue);
    return data;
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, updateSettings, applyHue }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { apiError };
